import { Inject, Injectable } from '@angular/core';
import { finalize, Observable, of, Subject } from 'rxjs';
import { WINDOW } from '../tokens/window';

export interface Location {
  lat: number;
  long: number;
}

const GEOLOCATION_EXPIRY = 60000;

@Injectable({ providedIn: 'root' })
export class LocationService {
  private location: Location = null;
  private location$: Observable<Location | null>;

  constructor(@Inject(WINDOW) private window: Window) {}

  public getLocation(timeout = 5000): Observable<Location> {
    if (this.location) {
      return of(this.location);
    } else {
      if (!this.location$) {
        // NOTE: firefox doesn't handle multiple requests well, so we do one at a time
        this.location$ = this.requestLocation(timeout).pipe(
          finalize(() => {
            this.location$ = null;
          })
        );
      }

      return this.location$;
    }
  }

  private requestLocation(timeout: number): Observable<Location> {
    const location$ = new Subject<Location>();

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.location = {
            lat: position.coords.latitude,
            long: position.coords.longitude,
          };
          this.setExpirationTimer();

          location$.next(this.location);
          location$.complete();
        },
        () => {
          location$.error('Error getting location');
          location$.complete();
        },
        {
          enableHighAccuracy: true,
          timeout, // milliseconds for how long to wait
        }
      );
    } else {
      location$.error('Geolocation is not available');
      location$.complete();
    }

    return location$.asObservable();
  }

  private setExpirationTimer() {
    this.window.setTimeout(() => {
      this.location = null;
    }, GEOLOCATION_EXPIRY);
  }
}
